import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/lib/states";
import { toast } from "sonner";
import { Trash2, Star, StarOff } from "lucide-react";

const inputCls =
  "mt-2 rounded-none border-0 border-b border-charcoal/30 bg-transparent focus-visible:ring-0 focus-visible:border-charcoal px-0 h-11";

export default function Account() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", email: "", business_name: "", phone: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [subInfo, setSubInfo] = useState(null);
  const [savingSub, setSavingSub] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscription/me");
        setSubInfo(data);
      } catch {/* fine */}
    })();
  }, []);

  const toggleSubscription = async () => {
    setSavingSub(true);
    try {
      if (subInfo?.subscribed) {
        await api.post("/subscription/unsubscribe");
        toast.success("You've been unsubscribed. We'll respect it.");
      } else {
        await api.post("/subscription/subscribe", { source: "account" });
        toast.success("Subscribed — welcome aboard.");
      }
      const { data } = await api.get("/subscription/me");
      setSubInfo(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSub(false);
    }
  };

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        email: user.email || "",
        business_name: user.business_name || "",
        phone: user.phone || "",
      });
    }
    loadAddresses();
  }, [user]);

  const loadAddresses = async () => {
    const { data } = await api.get("/addresses");
    setAddresses(data);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", profile);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwd.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      await api.post("/addresses", newAddr);
      setNewAddr({ label: "Home", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
      toast.success("Address added");
      loadAddresses();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setDefault = async (id) => {
    await api.post(`/addresses/${id}/default`);
    toast.success("Default address updated");
    loadAddresses();
  };

  const delAddress = async (id) => {
    await api.delete(`/addresses/${id}`);
    loadAddresses();
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="account-page">
      <p className="text-[13px] uppercase tracking-[0.22em] text-stone mb-4">Membership</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-12">Account.</h1>

      {/* Profile */}
      <section className="mb-16" data-testid="account-profile-section">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-[13px] uppercase tracking-[0.22em] text-stone">Profile</p>
          <p className="text-[12px] font-mono uppercase tracking-[0.22em] text-stone">Role · {user?.role}</p>
        </div>
        <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-borderSubtle p-8 grid md:grid-cols-2 gap-6" data-testid="profile-form">
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Full name</Label>
            <Input required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputCls} data-testid="profile-name-input"/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Email</Label>
            <Input required type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={inputCls} data-testid="profile-email-input"/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Business name (optional)</Label>
            <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} className={inputCls} data-testid="profile-business-input"/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Phone (optional)</Label>
            <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} data-testid="profile-phone-input"/>
          </div>
          <div className="md:col-span-2 mt-2">
            <button
              type="submit"
              disabled={savingProfile}
              className="px-8 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {savingProfile ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>
      </section>

      {/* Password */}
      <section className="mb-16" data-testid="account-password-section">
        <p className="text-[13px] uppercase tracking-[0.22em] text-stone mb-4">Password</p>
        <form onSubmit={changePassword} className="bg-white rounded-2xl border border-borderSubtle p-8 grid md:grid-cols-3 gap-6" data-testid="password-form">
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Current password</Label>
            <PasswordInput required value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={inputCls} data-testid="pwd-current-input"/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">New password</Label>
            <PasswordInput required minLength={6} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={inputCls} data-testid="pwd-new-input"/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Confirm new password</Label>
            <PasswordInput required minLength={6} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={inputCls} data-testid="pwd-confirm-input"/>
          </div>
          <div className="md:col-span-3 mt-2">
            <button
              type="submit"
              disabled={savingPwd}
              className="px-8 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full disabled:opacity-50"
              data-testid="change-pwd-btn"
            >
              {savingPwd ? "Updating…" : "Change password"}
            </button>
          </div>
        </form>
      </section>

      {/* Mailing list subscription */}
      <section className="mb-16" data-testid="account-subscription-section">
        <p className="text-[13px] uppercase tracking-[0.22em] text-stone mb-4">Mailing list</p>
        <div className="bg-white rounded-2xl border border-borderSubtle p-8 flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <p className="font-serif text-xl mb-2">
              {subInfo?.subscribed ? "You're subscribed." : "Get restock + sale alerts."}
            </p>
            <p className="text-sm text-stone leading-relaxed max-w-xl">
              {subInfo?.subscribed
                ? "You'll hear from us occasionally — restocks, new kits, small sales. One click unsubscribes. Or hit the button on the right."
                : "Occasional emails only. Restocks, new kits, small sales. One-click unsubscribe in every email. We never share your address."}
            </p>
            {subInfo?.opted_in_at && subInfo?.subscribed && (
              <p className="text-[12px] font-mono uppercase tracking-widest text-stone mt-3">
                Since {new Date(subInfo.opted_in_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </p>
            )}
          </div>
          <button
            onClick={toggleSubscription}
            disabled={savingSub}
            className={`shrink-0 px-6 py-3 tracking-[0.22em] text-xs uppercase transition-colors rounded-full disabled:opacity-50 ${
              subInfo?.subscribed
                ? "border border-borderSubtle text-charcoal hover:border-oxblood hover:text-oxblood"
                : "bg-charcoal text-bone hover:bg-oxblood"
            }`}
            data-testid="subscription-toggle-btn"
          >
            {savingSub ? "Saving…" : subInfo?.subscribed ? "Unsubscribe" : "Subscribe"}
          </button>
        </div>
      </section>

      {/* Addresses */}
      <section data-testid="account-addresses-section">
        <p className="text-[13px] uppercase tracking-[0.22em] text-stone mb-4">Shipping addresses</p>

        <div className="space-y-3 mb-6" data-testid="addresses-list">
          {addresses.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-borderSubtle p-5 flex justify-between items-start gap-4" data-testid={`address-${a.id}`}>
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium">{a.label}</p>
                  {a.is_default && (
                    <span className="text-[9px] font-mono uppercase tracking-widest bg-charcoal text-bone px-2 py-0.5 rounded-full" data-testid={`address-default-badge-${a.id}`}>
                      Default
                    </span>
                  )}
                </div>
                <p className="text-stone">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                <p className="text-stone">{a.city}, {a.state} {a.postal_code} · {a.country}</p>
              </div>
              <div className="flex items-center gap-1">
                {!a.is_default && (
                  <button
                    onClick={() => setDefault(a.id)}
                    className="p-2 text-stone hover:text-charcoal transition-colors"
                    data-testid={`set-default-${a.id}`}
                    aria-label="Set as default"
                    title="Set as default"
                  >
                    <StarOff size={16} strokeWidth={1.5}/>
                  </button>
                )}
                {a.is_default && (
                  <span className="p-2 text-charcoal" aria-label="Default address" title="Default address">
                    <Star size={16} strokeWidth={1.75} fill="currentColor"/>
                  </span>
                )}
                <button
                  onClick={() => delAddress(a.id)}
                  className="p-2 text-stone hover:text-oxblood transition-colors"
                  data-testid={`delete-address-${a.id}`}
                  aria-label="Delete address"
                >
                  <Trash2 size={16} strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          ))}
          {addresses.length === 0 && (
            <p className="text-sm text-stone">No addresses saved yet.</p>
          )}
        </div>

        <form onSubmit={addAddress} className="bg-white rounded-2xl border border-borderSubtle p-6 grid grid-cols-2 gap-5" data-testid="add-address-form">
          <div className="col-span-2">
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Label</Label>
            <Input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} className={inputCls}/>
          </div>
          <div className="col-span-2">
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Address line 1</Label>
            <Input required value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className={inputCls}/>
          </div>
          <div className="col-span-2">
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Address line 2</Label>
            <Input value={newAddr.line2} onChange={(e) => setNewAddr({ ...newAddr, line2: e.target.value })} className={inputCls}/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">City</Label>
            <Input required value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} className={inputCls}/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">State</Label>
            <Select value={newAddr.state} onValueChange={(v) => setNewAddr({ ...newAddr, state: v })}>
              <SelectTrigger className="mt-2 rounded-none border-0 border-b border-charcoal/30 bg-transparent px-0 h-11 focus:ring-0 focus:border-charcoal">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Postal</Label>
            <Input required value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} className={inputCls}/>
          </div>
          <div>
            <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Country</Label>
            <Input required value={newAddr.country} onChange={(e) => setNewAddr({ ...newAddr, country: e.target.value })} className={inputCls}/>
          </div>
          <div className="col-span-2 mt-2">
            <button type="submit" className="px-8 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full" data-testid="add-address-btn">
              Save address
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
